import { baseToApiShape, baseFromApiShape, baseInitNewForm, baseInitEditForm, withHeadOnlyPlaceholder } from '../formHelpers/base';

/**
 * GCP-specific flat → nested API payload.
 * Extends base with: spot VM, GPU accelerators, TPU, disk types, prefix.
 */
export function toApiShape(form) {
    const base = baseToApiShape(form);

    // Ensure accelerator fields on each node group
    const nodeGroups = (form.nodeGroups || []).map(g => ({
        ...g,
        accelerator_type: g.accelerator_type || null,
        accelerator_count: g.accelerator_count ?? 0,
    }));

    return {
        ...base,
        info: {
            ...base.info,
            prefix: form.prefix || '',
            // Marketplace image references (mirror azure) — without these the
            // catalog's image id never reaches the cluster doc, so a GCP
            // marketplace deploy falls back to a nonexistent "<image>-heidi".
            marketplace_head_image_id: form.marketplaceHeadImageId || null,
            marketplace_compute_image_id: form.marketplaceComputeImageId || null,
        },
        data: {
            ...base.data,
            spot_vm: form.spotVm || false,
            server: {
                ...base.data.server,
                disk_type: form.serverDiskType || '',
                accelerator_type: form.serverAcceleratorType || null,
                accelerator_count: form.serverAcceleratorCount ?? 0,
            },
            network: {
                ...base.data.network,
                enable_zone_fallback: form.enableZoneFallback ?? false,
            },
            node: {
                ...base.data.node,
                disk_type: form.nodeDiskType || '',
                group: withHeadOnlyPlaceholder(nodeGroups, form),
            },
            tpu: form.tpuEnabled ? {
                enabled: true,
                accelerator_type: form.tpuAcceleratorType || 'v2-8',
                runtime_version: form.tpuRuntimeVersion || 'tpu-vm-tf-2.13.0',
                count: form.tpuCount || 1,
                cidr_block: form.tpuCidrBlock || null,
                enable_external_ips: form.tpuEnableExternalIps || false,
                preemptible: form.tpuPreemptible || false,
                spot: form.tpuSpot || false,
            } : { enabled: false },
        },
    };
}

/**
 * GCP-specific nested → flat form state.
 */
export function fromApiShape(cluster) {
    return {
        ...baseFromApiShape(cluster),

        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,
        spotVm: cluster.data?.spot_vm || false,

        enableZoneFallback: cluster.data?.network?.enable_zone_fallback ?? false,

        serverDiskType: cluster.data?.server?.disk_type || '',
        serverAcceleratorType: cluster.data?.server?.accelerator_type || null,
        serverAcceleratorCount: cluster.data?.server?.accelerator_count ?? 0,

        nodeDiskType: cluster.data?.node?.disk_type || '',

        tpuEnabled: cluster.data?.tpu?.enabled || false,
        tpuAcceleratorType: cluster.data?.tpu?.accelerator_type || 'v2-8',
        tpuRuntimeVersion: cluster.data?.tpu?.runtime_version || 'tpu-vm-tf-2.13.0',
        tpuCount: cluster.data?.tpu?.count || 1,
        tpuCidrBlock: cluster.data?.tpu?.cidr_block || '',
        tpuEnableExternalIps: cluster.data?.tpu?.enable_external_ips || false,
        tpuPreemptible: cluster.data?.tpu?.preemptible || false,
        tpuSpot: cluster.data?.tpu?.spot || false,
    };
}

export function initNewForm(provider, config) {
    // Seed enableZoneFallback from org / platform default. Mirrors how
    // baseInitNewForm reads enableCapacityReservation from
    // fastCapacityValidationDefault. Both live in the same settings
    // record so an admin who turns on "default to capacity reservation"
    // and "default to zone fallback" gets BOTH toggles pre-checked.
    const orgZF = config?.orgClusterSettings?.clusterSettings?.zoneFallbackDefault;
    const platformZF = config?.clusterSettings?.zoneFallbackDefault;
    return {
        ...baseInitNewForm(provider, config),
        spotVm: false,
        // Marketplace image references (if marketplace stack) — mirror azure so
        // the catalog's ParaTools image id is carried onto the cluster.
        marketplaceHeadImageId: config.marketplaceConfig?.marketplace_head_image_id || null,
        marketplaceComputeImageId: config.marketplaceConfig?.marketplace_compute_image_id
            || config.marketplaceConfig?.marketplace_head_image_id || null,
        enableZoneFallback: (orgZF === true || platformZF === true),
        serverDiskType: '',
        nodeDiskType: '',
        serverAcceleratorType: null,
        serverAcceleratorCount: 0,
        tpuEnabled: false,
    };
}

export function initEditForm(cluster, config) {
    return {
        ...baseInitEditForm(cluster, config),

        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,
        spotVm: cluster.data?.spot_vm || false,
        enableZoneFallback: cluster.data?.network?.enable_zone_fallback ?? false,
        serverDiskType: cluster.data?.server?.disk_type || '',
        serverAcceleratorType: cluster.data?.server?.accelerator_type || null,
        serverAcceleratorCount: cluster.data?.server?.accelerator_count ?? 0,
        nodeDiskType: cluster.data?.node?.disk_type || '',

        tpuEnabled: cluster.data?.tpu?.enabled || false,
        tpuAcceleratorType: cluster.data?.tpu?.accelerator_type || 'v2-8',
        tpuRuntimeVersion: cluster.data?.tpu?.runtime_version || 'tpu-vm-tf-2.13.0',
        tpuCount: cluster.data?.tpu?.count || 1,
        tpuCidrBlock: cluster.data?.tpu?.cidr_block || '',
        tpuEnableExternalIps: cluster.data?.tpu?.enable_external_ips || false,
        tpuPreemptible: cluster.data?.tpu?.preemptible || false,
        tpuSpot: cluster.data?.tpu?.spot || false,
    };
}

/** Returns true if instance supports guest accelerators (GPU) */
export function isN1Instance(instanceId) {
    return Boolean(instanceId && instanceId.startsWith('n1-'));
}
